import { useQuery } from "@apollo/client";
import {
  Autocomplete,
  Chip,
  FormControl,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useState, useMemo } from "react";
import { TAGS_QUERY } from "queries/tags";
import { DocumentNode } from "graphql";

interface UnifiedTagsInputProps {
  value?: string[] | null;
  onChange: (tags: string[]) => void;
  label?: string;
  onRequestApply?: () => void;
  // Optional GraphQL query to fetch available tags
  tagsQuery?: DocumentNode;
  // Optional query configuration when using a tagsQuery
  queryConfig?: {
    tenantId?: string;
    additionalSearch?: string;
  };
  placeholder?: string;
  size?: "small" | "medium";
}

export function UnifiedTagsInput({
  value,
  onChange,
  label = "Tags",
  onRequestApply,
  tagsQuery,
  queryConfig,
  placeholder = "Press enter or comma to add",
  size = "small",
}: UnifiedTagsInputProps) {
  const [inputValue, setInputValue] = useState("");

  // Build search query for backend
  const searchQuery = useMemo(() => {
    if (!tagsQuery) return null;

    // Workflow tags (type:Workflow) don't require tenantId - they're staff-only internal tags
    const isWorkflowTags =
      queryConfig?.additionalSearch?.includes("type:Workflow");

    // All other tag types require tenantId for security
    if (!queryConfig?.tenantId && !isWorkflowTags) {
      return null;
    }

    let query = "";
    if (queryConfig?.tenantId) {
      query = `tenantId:${queryConfig.tenantId}`;
    }

    if (queryConfig?.additionalSearch) {
      query += query
        ? ` ${queryConfig.additionalSearch}`
        : queryConfig.additionalSearch;
    }

    return query;
  }, [tagsQuery, queryConfig]);

  // Query backend tags if configured
  const { data, loading } = useQuery(tagsQuery || TAGS_QUERY, {
    fetchPolicy: "cache-first",
    variables: {
      search: searchQuery,
      sort: [{ field: "name", sort: "asc" }],
      page: 0,
      pageSize: 100,
    },
    skip: !tagsQuery || !searchQuery,
  });

  // Get available tags from backend or empty array
  const availableTags = useMemo(() => {
    if (!data?.tags?.items) return [];
    return data.tags.items.map((tag: any) => tag.name).sort();
  }, [data]);

  // Combine backend tags with current value for options
  // This ensures user-entered tags that aren't in backend still show up
  const allOptions = useMemo(() => {
    const currentTags = value ?? [];
    const combined = new Set([...availableTags, ...currentTags]);
    return Array.from(combined).sort();
  }, [availableTags, value]);

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: string[]
  ) => {
    // Sort and deduplicate
    const uniqueTags = Array.from(new Set(newValue)).sort();
    onChange(uniqueTags);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Handle comma key
    if (event.key === "," && inputValue.trim()) {
      event.preventDefault();
      const newTag = inputValue.trim();
      const currentTags = value ?? [];

      // Add the new tag if it doesn't already exist
      if (!currentTags.includes(newTag)) {
        const newTags = [...currentTags, newTag];
        // Sort and deduplicate
        const uniqueTags = Array.from(new Set(newTags)).sort();
        onChange(uniqueTags);
      }

      // Clear the input
      setInputValue("");
    }
    // Handle Enter key
    else if (event.key === "Enter") {
      // If there's no input, apply filters
      if (!inputValue.trim() && onRequestApply) {
        event.preventDefault();
        onRequestApply();
      }
      // Otherwise let Autocomplete handle tag creation naturally
    }
    // Handle tab key
    else if (event.key === "Tab" && inputValue.trim()) {
      // Don't prevent default - we want tab to still move to next field
      const newTag = inputValue.trim();
      const currentTags = value ?? [];

      // Add the new tag if it doesn't already exist
      if (!currentTags.includes(newTag)) {
        const newTags = [...currentTags, newTag];
        // Sort and deduplicate
        const uniqueTags = Array.from(new Set(newTags)).sort();
        onChange(uniqueTags);
      }

      // Clear the input
      setInputValue("");
    }
  };

  return (
    <FormControl fullWidth>
      <Autocomplete
        size={size}
        multiple
        freeSolo
        options={allOptions}
        value={value ?? []}
        inputValue={inputValue}
        loading={loading && !!tagsQuery}
        onInputChange={(event, newInputValue, reason) => {
          // Don't clear the input when a tag is added
          if (reason !== "reset") {
            setInputValue(newInputValue);
          }
        }}
        onChange={(event, newValue, reason) => {
          handleChange(event, newValue);
          // Clear the input after adding a tag
          if (reason === "createOption" || reason === "selectOption") {
            setInputValue("");
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            autoComplete="off"
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && tagsQuery ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return (
              <Chip key={key} size="small" label={option} {...chipProps} />
            );
          })
        }
        // Custom filter to handle free-form entry
        filterOptions={(options, state) => {
          // If input matches an existing option exactly, don't duplicate it
          const inputLower = state.inputValue.toLowerCase().trim();
          const filtered = options.filter((option) =>
            option.toLowerCase().includes(inputLower)
          );

          // If the input doesn't match any existing option and has value,
          // add it as a createOption
          if (
            state.inputValue.trim() &&
            !options.some((option) => option.toLowerCase() === inputLower)
          ) {
            filtered.push(state.inputValue.trim());
          }

          return filtered;
        }}
      />
    </FormControl>
  );
}
